"use client";

import { useState, useEffect } from "react";
import Link from "next/link";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, Download, Sparkles, ArrowUpRight } from "lucide-react";
import { cn } from "@/lib/utils";
import { useTheme } from "@/hooks/useTheme";
import { ThemeText } from "@/components/ui/theme-utils";

interface NavLink {
  href: string;
  label: string;
}

interface NavbarProps {
  links?: NavLink[];
  showDownloadButton?: boolean;
  isHomePage?: boolean;
}

export function Navbar({
  links = [
    { href: "#features", label: "Features" },
    { href: "#how-it-works", label: "How it works" },
    { href: "#security", label: "Security" },
    { href: "#faq", label: "FAQ" }
  ],
  showDownloadButton = true,
  isHomePage = true
}: NavbarProps) {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const { isDark } = useTheme();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const navLinks = links.map((link) =>
    !isHomePage && link.href.startsWith("#")
      ? { href: `/${link.href}`, label: link.label }
      : link
  );

  return (
    <header
      className={cn(
        "fixed top-0 left-0 right-0 z-50 transition-all duration-300",
        scrolled ? "pt-3" : "pt-5"
      )}
    >
      {/* Announcement bar */}
      <AnimatePresence>
        {!scrolled && (
          <motion.div
            initial={{ opacity: 0, y: -10, height: 0 }}
            animate={{ opacity: 1, y: 0, height: "auto" }}
            exit={{ opacity: 0, y: -10, height: 0 }}
            className={cn(
              "border-b",
              isDark ? "bg-white/[0.02] border-white/5" : "bg-stone-50/80 border-stone-200/80"
            )}
          >
            <div className="container mx-auto px-5 sm:px-6 py-2 flex items-center justify-center gap-2 text-xs sm:text-sm">
              <span className="inline-flex h-5 items-center gap-1 rounded-full bg-gradient-to-r from-brand-700 to-brand-900 px-2 text-[10px] font-semibold uppercase tracking-wider text-white">
                <Sparkles className="h-3 w-3" />
                New
              </span>
              <ThemeText variant="subtle">
                Auto-retry v3 is live — zero monitoring on every transfer
              </ThemeText>
              <Link
                href="#features"
                className={cn(
                  "hidden sm:inline-flex items-center gap-0.5 font-medium",
                  isDark ? "text-brand-200 hover:text-white" : "text-brand-700 hover:text-brand-900"
                )}
              >
                Read more
                <ArrowUpRight className="h-3.5 w-3.5" />
              </Link>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Navbar */}
      <div className="container mx-auto px-5 sm:px-6">
        <nav
          className={cn(
            "flex items-center justify-between gap-4 rounded-full transition-all duration-300",
            scrolled
              ? "px-3 py-2 backdrop-blur-xl shadow-elevated"
              : "px-4 py-2.5",
            isDark
              ? scrolled
                ? "bg-[#0E0E14]/80 border border-white/10"
                : "bg-white/[0.04] border border-white/5"
              : scrolled
              ? "bg-white/85 border border-stone-200/80"
              : "bg-white/40 border border-stone-200/60"
          )}
        >
          {/* Logo */}
          <Link href="/" className="flex items-center gap-2.5 pl-2 pr-1 group">
            <img
              src="/images/zset-logo.png"
              alt="Zset"
              width={32}
              height={32}
              className="h-8 w-8 rounded-full dark:invert transition-transform group-hover:scale-[1.04]"
            />
            <span className="text-lg font-semibold tracking-tight">Zset</span>
          </Link>

          {/* Center links */}
          <div className="hidden lg:flex items-center gap-1">
            {navLinks.map((link) => (
              <Link
                key={link.href}
                href={link.href}
                className={cn(
                  "px-4 py-1.5 text-sm font-medium rounded-full transition-colors",
                  isDark
                    ? "text-white/70 hover:text-white hover:bg-white/[0.06]"
                    : "text-stone-600 hover:text-stone-900 hover:bg-stone-100"
                )}
              >
                {link.label}
              </Link>
            ))}
          </div>

          {/* Right side */}
          <div className="flex items-center gap-2">
            {showDownloadButton && (
              <Link
                href="/downloads"
                className={cn(
                  "group inline-flex items-center gap-1.5 px-4 py-1.5 rounded-full text-sm font-medium transition-all",
                  "bg-gradient-to-r from-brand-700 via-brand-800 to-brand-900 text-white shadow-glow-sm hover:shadow-glow",
                  "ring-1 ring-inset ring-white/10"
                )}
              >
                Download
                <Download className="h-3.5 w-3.5 transition-transform group-hover:translate-y-0.5" />
              </Link>
            )}
            <button
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              className={cn(
                "lg:hidden inline-flex h-9 w-9 items-center justify-center rounded-full",
                isDark ? "bg-white/5 text-white" : "bg-stone-100 text-stone-900"
              )}
              aria-label="Toggle menu"
            >
              {isMenuOpen ? <X className="h-4 w-4" /> : <Menu className="h-4 w-4" />}
            </button>
          </div>
        </nav>
      </div>

      {/* Mobile menu */}
      <AnimatePresence>
        {isMenuOpen && (
          <motion.div
            initial={{ opacity: 0, y: -8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            className="container mx-auto px-5 sm:px-6 mt-2 lg:hidden"
          >
            <div
              className={cn(
                "rounded-2xl p-3 shadow-elevated backdrop-blur-xl",
                isDark
                  ? "bg-[#0E0E14]/90 border border-white/10"
                  : "bg-white/95 border border-stone-200"
              )}
            >
              <div className="flex flex-col">
                {navLinks.map((link) => (
                  <Link
                    key={link.href}
                    href={link.href}
                    onClick={() => setIsMenuOpen(false)}
                    className={cn(
                      "px-3 py-2.5 text-sm font-medium rounded-xl transition-colors",
                      isDark
                        ? "text-white/80 hover:text-white hover:bg-white/5"
                        : "text-stone-700 hover:text-stone-900 hover:bg-stone-100"
                    )}
                  >
                    {link.label}
                  </Link>
                ))}
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
}
